// Brand-styled confirmation dialog. Drop-in replacement for window.confirm().
//
// Usage:
//   import { useConfirm } from "@/components/ui/use-confirm";
//   const { confirm, ConfirmHost } = useConfirm();
//   ...
//   const ok = await confirm({ title: "Delete?", description: "...", destructive: true });
//   if (ok) doDelete();
//   ...
//   return ( <> ...rest of JSX...  <ConfirmHost /> </> )
//
// Single host per component tree. Works with any React 18+ tree.
import { useCallback, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const DEFAULTS = {
  title: "Are you sure?",
  description: "",
  confirmLabel: "Confirm",
  cancelLabel: "Cancel",
  destructive: false,
};

export function useConfirm() {
  const [state, setState] = useState({ open: false, ...DEFAULTS });
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ open: true, ...DEFAULTS, ...opts });
    });
  }, []);

  const handleConfirm = () => {
    resolverRef.current?.(true);
    resolverRef.current = null;
    setState((s) => ({ ...s, open: false }));
  };

  const handleCancel = () => {
    resolverRef.current?.(false);
    resolverRef.current = null;
    setState((s) => ({ ...s, open: false }));
  };

  const ConfirmHost = () => (
    <AlertDialog
      open={state.open}
      onOpenChange={(o) => { if (!o) handleCancel(); }}
    >
      <AlertDialogContent
        data-testid="confirm-dialog"
        className="rounded-2xl border-border bg-card sm:max-w-md"
      >
        <AlertDialogHeader className="space-y-1.5">
          <AlertDialogTitle
            data-testid="confirm-dialog-title"
            className="text-[16px] font-semibold tracking-tight text-foreground"
          >
            {state.title}
          </AlertDialogTitle>
          {state.description && (
            <AlertDialogDescription
              data-testid="confirm-dialog-description"
              className="text-[13.5px] leading-relaxed text-muted-foreground"
            >
              {state.description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter className="mt-2 gap-2 sm:gap-2">
          <AlertDialogCancel
            data-testid="confirm-dialog-cancel"
            onClick={handleCancel}
            className="h-9 rounded-lg border-border text-[13px] font-medium"
          >
            {state.cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-dialog-confirm"
            onClick={handleConfirm}
            className={[
              "h-9 rounded-lg text-[13px] font-medium",
              state.destructive
                ? "bg-rose-500 text-white hover:bg-rose-500/90 focus-visible:ring-rose-500/40"
                : "bg-primary text-primary-foreground hover:bg-primary/90",
            ].join(" ")}
          >
            {state.confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { confirm, ConfirmHost };
}
